// Календарная математика на native Date/Intl — без date-библиотеки.
// Экземпляр обслуживает одну команду в одном городе, поэтому «день» — это
// локальный день, а не сутки UTC; на API уходит UTC ISO. Неделя с понедельника.

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export function startOfWeek(d: Date): Date {
  const day = startOfDay(d)
  // getDay(): 0 = воскресенье — сдвигаем к понедельнику
  const shift = (day.getDay() + 6) % 7
  return addDays(day, -shift)
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

export function addMinutes(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 60_000)
}

export function addMonths(d: Date, n: number): Date {
  // День фиксируем 1-м: якорь навигации по месяцам, а не «то же число»
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isToday(d: Date): boolean {
  return isSameDay(d, new Date())
}

export function minutesSinceMidnight(d: Date): number {
  return d.getHours() * 60 + d.getMinutes()
}

/** Момент внутри суток `day` по минутам от полуночи (может выходить за сутки). */
export function atMinutes(day: Date, minutes: number): Date {
  const base = startOfDay(day)
  return new Date(base.getTime() + minutes * 60_000)
}

export function weekDays(anchor: Date): Date[] {
  const start = startOfWeek(anchor)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

export interface MonthCell {
  date: Date
  outside: boolean
}

/** Сетка месяца: полные недели пн–вс, покрывающие месяц (28–42 ячейки). */
export function monthGrid(anchor: Date): MonthCell[] {
  const first = startOfMonth(anchor)
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
  const gridStart = startOfWeek(first)
  const gridEnd = addDays(startOfWeek(last), 7)
  const month = first.getMonth()
  const cells: MonthCell[] = []
  for (let d = gridStart; d < gridEnd; d = addDays(d, 1)) {
    cells.push({ date: d, outside: d.getMonth() !== month })
  }
  return cells
}

export function toISODate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** `YYYY-MM-DD` → локальная полночь; мусор → null (для validateSearch). */
export function fromISODate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const d = new Date(year, month - 1, day)
  // Date перекатывает out-of-range компоненты (13-й месяц → январь) — сверяем
  const valid = d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
  return valid ? d : null
}

export interface Interval {
  startsAt: Date
  endsAt: Date
}

/**
 * Доменное правило пересечения (schedule.ConflictWarning): интервалы
 * пересекаются, если один начинается раньше конца другого и кончается позже его
 * начала. Касание границами пересечением НЕ считается.
 */
export function overlaps(a: Interval, b: Interval): boolean {
  return a.startsAt < b.endsAt && a.endsAt > b.startsAt
}

/** Занимает ли интервал данный локальный день. Ночной выход виден в обоих днях. */
export function coversDay(iv: Interval, day: Date): boolean {
  const dayStart = startOfDay(day)
  return overlaps(iv, { startsAt: dayStart, endsAt: addDays(dayStart, 1) })
}

// Кэшированные Intl-форматтеры: создание — дорогое, вызовов — сотни на рендер.
const timeFmt = new Intl.DateTimeFormat('ru', { hour: '2-digit', minute: '2-digit' })
const dayFmt = new Intl.DateTimeFormat('ru', { weekday: 'long', day: 'numeric', month: 'long' })
const dayShortFmt = new Intl.DateTimeFormat('ru', { weekday: 'short', day: 'numeric' })
const monthTitleFmt = new Intl.DateTimeFormat('ru', { month: 'long', year: 'numeric' })
const dayMonthFmt = new Intl.DateTimeFormat('ru', { day: 'numeric', month: 'long' })
const fullDateFmt = new Intl.DateTimeFormat('ru', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

export function fmtTime(d: Date): string {
  return timeFmt.format(d)
}

/** `13 июня, вторник` — крупный заголовок дня в верхней панели */
export function fmtDay(d: Date): string {
  const parts = dayFmt.formatToParts(d)
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? ''
  return `${dayMonthFmt.format(d)}, ${weekday}`
}

/** `пн 20` — колонки недели */
export function fmtDayShort(d: Date): string {
  return dayShortFmt.format(d)
}

/** `июнь 2026 г.` (хвост «г.» — норма ru-Intl) */
export function fmtMonthTitle(d: Date): string {
  return monthTitleFmt.format(startOfMonth(d))
}

/** `14–20 июля 2026` / `28 июля – 3 августа 2026` — заголовок недели */
export function fmtWeekTitle(anchor: Date): string {
  const days = weekDays(anchor)
  const first = days[0] as Date
  const last = days[6] as Date
  const year = last.getFullYear()
  if (first.getMonth() === last.getMonth()) {
    return `${first.getDate()}–${last.getDate()} ${dayMonthFmt.format(last).replace(/^\d+ /, '')} ${year}`
  }
  return `${dayMonthFmt.format(first)} – ${dayMonthFmt.format(last)} ${year}`
}

/** `20 июля 2026` — подписи вне календарной сетки */
export function fmtFullDate(d: Date): string {
  return fullDateFmt.format(d)
}

/**
 * Интервал для карточек и списков:
 * - в один день:   `20 июля · 14:00–15:00`
 * - через полночь: `20 июля 22:00 – 21 июля 01:30`
 */
export function fmtInterval(iv: Interval): string {
  if (isSameDay(iv.startsAt, iv.endsAt)) {
    return `${dayMonthFmt.format(iv.startsAt)} · ${fmtTime(iv.startsAt)}–${fmtTime(iv.endsAt)}`
  }
  return `${dayMonthFmt.format(iv.startsAt)} ${fmtTime(iv.startsAt)} – ${dayMonthFmt.format(iv.endsAt)} ${fmtTime(iv.endsAt)}`
}

const MINUTE_MS = 60_000

/** Продолжительность: `45 мин` / `1 ч 30 мин` / `2 ч`. */
export function fmtDuration(iv: Interval): string {
  const minutes = Math.max(0, Math.round((iv.endsAt.getTime() - iv.startsAt.getTime()) / MINUTE_MS))
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} мин`
  if (rest === 0) return `${hours} ч`
  return `${hours} ч ${rest} мин`
}
